import { useState } from "react";
import { Upload, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useSite } from "../../context/SiteContext";

function fileToDataUrl(file, maxW = 1920) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxW / img.width);
        const canvas = document.createElement("canvas");
        canvas.width = Math.round(img.width * scale);
        canvas.height = Math.round(img.height * scale);
        canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
        resolve(canvas.toDataURL("image/jpeg", 0.85));
      };
      img.onerror = reject;
      img.src = e.target.result;
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function SlidesManager() {
  const { slides, reload } = useSite();
  const [busy, setBusy] = useState(false);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      for (const f of files) { const image = await fileToDataUrl(f); await api.create("slides", { image, caption: "" }); }
      toast.success(`${files.length} slide(s) added`);
      await reload();
    } catch { toast.error("Upload failed"); }
    finally { setBusy(false); e.target.value = ""; }
  };
  const saveCaption = async (s, caption) => { try { await api.update("slides", s.id, { image: s.image, caption }); await reload(); } catch { toast.error("Failed"); } };
  const remove = async (id) => { try { await api.remove("slides", id); toast.success("Removed"); await reload(); } catch { toast.error("Failed"); } };

  return (
    <div data-testid="slides-manager">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
        <div>
          <h2 className="font-serif text-2xl text-forest">{slides.length} Home Slide{slides.length !== 1 ? "s" : ""}</h2>
          <p className="text-muted text-sm mt-1">Rectangular banner images that auto-slide on the homepage hero.</p>
        </div>
        <label className="inline-flex items-center gap-2 bg-gold text-forest px-6 py-3 rounded-full text-sm uppercase tracking-widest hover:-translate-y-0.5 transition-transform cursor-pointer">
          {busy ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} Add Slides
          <input data-testid="upload-slides" type="file" accept="image/*" multiple onChange={onUpload} className="hidden" disabled={busy} />
        </label>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {slides.map((s) => (
          <div key={s.id || s.image} data-testid="slide-admin-item" className="bg-white border border-line rounded-sm overflow-hidden">
            <div className="aspect-[16/6] bg-bone overflow-hidden"><img src={s.image} alt={s.caption} className="w-full h-full object-cover" /></div>
            <div className="p-3 flex items-center gap-3">
              <input defaultValue={s.caption} placeholder="Caption (optional)" onBlur={(e) => e.target.value !== s.caption && saveCaption(s, e.target.value)} className="flex-1 text-sm bg-transparent border-b border-line focus:border-gold outline-none py-1" />
              <button data-testid="delete-slide-btn" onClick={() => remove(s.id)} className="text-xs text-destructive inline-flex items-center gap-1"><Trash2 size={13} /> Remove</button>
            </div>
          </div>
        ))}
        {slides.length === 0 && <p className="p-8 text-muted col-span-full">No slides yet. Add some above.</p>}
      </div>
    </div>
  );
}
